import React from "react";

/**
 * FAQSection — адаптивний блок з FAQ щодо сертифікатів БПР.
 *
 * Використання:
 * <FAQSection Group={Group} />
 * - Group (опціонально): ваш існуючий компонент секції з заголовком (як у сертифікатах).
 * - Якщо Group не передано, секція рендериться як самостійний блок.
 *
 * Особливості:
 * - Працює як на мобільних, так і на десктопі.
 * - Використовує <details>/<summary> для доступних акордеонів.
 * - Обмежена ширина контенту для зручного читання на великих екранах.
 */
export default function FAQSection({ Group }) {
    const Wrapper = ({ children }) => {
        if (Group) {
            return (
                <Group id="grp-faq" title="FAQ — Сертифікати БПР" defaultOpen>
                    {children}
                </Group>
            );
        }
        return (
            <section className="faq-section">
                <h2 className="faq-title">FAQ — Сертифікати БПР</h2>
                {children}
            </section>
        );
    };

    return (
        <Wrapper>
            <style>{`
        /* Контейнер */
        .faq-container { max-width: 840px; margin: 8px auto 0; padding: 0 12px; }
        .faq-title { font-size: 20px; font-weight: 700; margin: 0 0 8px; }

        /* Акордеон */
        .faq-item { border: 1px solid rgba(0,0,0,.08); border-radius: 12px; background: var(--faq-bg, #fff); box-shadow: 0 1px 2px rgba(0,0,0,.05); }
        .faq-item + .faq-item { margin-top: 10px; }
        .faq-item summary { cursor: pointer; list-style: none; padding: 14px 16px; font-weight: 600; line-height: 1.35; position: relative; }
        .faq-item summary::-webkit-details-marker { display: none; }
        .faq-item[open] summary { border-bottom: 1px solid rgba(0,0,0,.06); }
        .faq-item .faq-content { padding: 12px 16px 16px; font-size: 14px; line-height: 1.6; }
        .faq-item .faq-content p { margin: 0 0 10px; }
        .faq-item .faq-content ul { margin: 0 0 10px 18px; }
        .faq-item .faq-content li + li { margin-top: 6px; }
        .faq-note { background: rgba(255, 221, 87, .15); border: 1px solid rgba(255,221,87,.6); border-radius: 10px; padding: 10px 12px; font-size: 13px; }
        .faq-link { text-decoration: underline; }
        .faq-inline-muted { opacity: .75; }

        /* Маркери у summary */
        .faq-item summary:after { content: "▾"; position: absolute; right: 14px; top: 14px; transition: transform .2s ease; }
        .faq-item[open] summary:after { transform: rotate(180deg); }

        

        /* Мобільні покращення */
        @media (max-width: 768px) {
          .faq-title { font-size: 18px; }
          .faq-item summary { padding: 14px; }
          .faq-item .faq-content { padding: 12px 14px 14px; }
        }
      `}</style>

            <div className="faq-container">
                {/* 1 */}
                <details className="faq-item">
                    <summary>Чому я не бачу своїх сертифікатів у портфоліо?</summary>
                    <div className="faq-content">
                        <p>Існує кілька причин, чому ваші сертифікати можуть ще не відображатися:</p>
                        <ul>
                            <li>
                                <strong>Перевірте правильність даних.</strong> Уважно звірте рік пошуку, написання прізвища, імені та по батькові, а також електронну адресу, на яку зареєстровано участь у заході. Якщо ви <strong>змінювали прізвище</strong>, обов’язково шукайте сертифікати <strong>за тим прізвищем, яке було актуальним на момент проходження заходу</strong>. Центр тестування вносить інформацію саме за даними, переданими організатором на момент звітування.
                            </li>
                            <li>
                                <strong>Сертифікати ще не оприлюднені Центром тестування при МОЗ України.</strong> За чинним порядком, організатор має <strong>до 20 робочих днів</strong> після заходу для подання звіту, а Центр тестування — орієнтовно стільки ж часу для перевірки та публікації результатів. Тож у деяких випадках оновлення може тривати <strong>до 1–2 місяців</strong>.
                            </li>
                            <li>
                                <strong>Можливі технічні чи реєстраційні неточності.</strong> Іноді провайдери допускають помилки при звітуванні (наприклад, некоректний номер заходу чи дані учасника). У таких випадках публікація затримується до моменту корекції звіту.
                            </li>
                        </ul>
                        <p className="faq-note">
                            <strong>Зверніть увагу:</strong> навіть якщо у вас є друкована або електронна копія сертифіката, це <em>ще не означає</em>, що він офіційно зарахований у систему БПР. Враховуються лише ті сертифікати, які <strong>оприлюднено Центром тестування МОЗ України</strong> у державному реєстрі.
                        </p>
                        <p>
                            Для перевірки офіційної наявності вашого сертифіката скористайтеся сайтом: {" "}
                            <a className="faq-link" href="https://www.testcentr.org.ua" target="_blank" rel="noreferrer">https://www.testcentr.org.ua</a>
                        </p>
                    </div>
                </details>

                {/* 2 */}
                <details className="faq-item">
                    <summary>Коли сертифікат з’явиться у вашому портфоліо?</summary>
                    <div className="faq-content">
                        <p>Одразу після того, як <strong>Центр тестування опублікує</strong> інформацію про захід у реєстрі, система <strong>автоматично підтягне</strong> ваш сертифікат. Оновлення відбувається <strong>в режимі реального часу</strong> — вам не потрібно нічого робити вручну.</p>
                    </div>
                </details>

                {/* 3 */}
                <details className="faq-item">
                    <summary>Чому деякі сертифікати не знаходяться навіть після оновлення?</summary>
                    <div className="faq-content">
                        <ul>
                            <li><strong>Захід ще не завершив перевірку Центром тестування.</strong> Якщо минуло менше місяця після події, сертифікати можуть ще не бути оприлюднені.</li>
                            <li><strong>Не всі заходи проходять через Центр тестування.</strong> Наприклад, деякі програми НСЗУ чи державних інститутів мають інший порядок звітування та можуть не потрапляти до центрального реєстру.</li>
                            <li><strong>Інший формат освітнього заходу.</strong> Для тематичних удосконалень чи стажувань облік відбувається за окремими процедурами, і вони не завжди відображаються у базі Центру тестування.</li>
                        </ul>
                        <p>
                            У таких випадках ви можете <strong>самостійно додати захід вручну</strong> у своє портфоліо — система дозволяє прикріпити скан-копію сертифіката, вказати кількість балів та короткий опис заходу.
                        </p>
                    </div>
                </details>

                {/* 4 */}
                <details className="faq-item">
                    <summary>Коротко: що робити, якщо сертифікат не знаходиться</summary>
                    <div className="faq-content">
                        <ul>
                            <li>Переконайтеся, що минуло <strong>щонайменше 20 робочих днів</strong> після заходу.</li>
                            <li>Перевірте правильність введених даних (у т.ч. старе прізвище, якщо змінювали).</li>
                            <li>Знайдіть сертифікат у реєстрі: {" "}
                                <a className="faq-link" href="https://www.testcentr.org.ua" target="_blank" rel="noreferrer">testcentr.org.ua</a>
                            </li>
                            <li>Якщо не знайдено — <strong>додайте вручну</strong> через кабінет («Моє портфоліо»).</li>
                            <li>Якщо й після 2 місяців сертифікат не з’явився — <strong>зверніться до організатора</strong>.</li>
                        </ul>
                    </div>
                </details>

                {/* 5 */}
                <details className="faq-item">
                    <summary>Порада від служби підтримки</summary>
                    <div className="faq-content">
                        <p>
                            Пам’ятайте, що <strong>наявність сертифіката у портфоліо не залежить від платформи напряму</strong> — ми лише відображаємо офіційно оприлюднені дані Центру тестування МОЗ. Будь ласка, дайте системі час на оновлення перед тим, як звертатися до служби підтримки.
                        </p>
                        <p className="faq-inline-muted">(Оновлення з реєстру підтягуються автоматично після публікації.)</p>
                    </div>
                </details>
            </div>
        </Wrapper>
    );
}
